import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowLeft, ArrowRight, ArrowDown, MapPin, Phone, Mail, Leaf, ShieldCheck, Heart, Award, Mountain, Beef } from 'lucide-react';
import { COMPANY, REFERENCES } from '../mock/data';
import { Reveal } from '../hooks/useReveal';
import MagneticButton from '../components/MagneticButton';
import SafeImg from '../components/SafeImg';
import SEO, { breadcrumbLd, serviceLd } from '../components/SEO';

// ─── Image sources ──────────────────────────────────────────────────────
// 1) Local farm photo (verified — exists in /public/img/achermann/)
const IMG_HOF_LOCAL = '/img/achermann/imgi_31_Hof_PHOTO_2023_02_15_16_23_54.jpg';

// 2) Curated Unsplash IDs — themed to Swiss alpine cattle / farm / mountain
//    Every one gets a SafeImg fallback so broken URLs never leave an empty box.
//    All URLs use the ?auto=format&fit=crop CDN parameters that Unsplash serves
//    reliably without signing.
const UNSPLASH = (id, w = 1600) =>
  `https://images.unsplash.com/${id}?auto=format&fit=crop&w=${w}&q=85`;

// Alps / mountains / lakes near Seelisberg (verified widely-used IDs)
const IMG_HERO_ALPS   = UNSPLASH('photo-1500595046743-cd271d694d30', 1920);   // green field + cattle wide
const IMG_ALPS_PANORAMA = UNSPLASH('photo-1464822759023-fed622ff2c3b', 1600); // alpine mountains
const IMG_MOUNTAIN    = UNSPLASH('photo-1519681393784-d120267933ba', 1600);   // dramatic peaks
const IMG_LAKE_ALPS   = UNSPLASH('photo-1506905925346-21bda4d32df4', 1600);   // mountain lake

// Cattle / cows on pasture
const IMG_COWS        = UNSPLASH('photo-1500595046743-cd271d694d30', 1600);
const IMG_COWS_CLOSE  = UNSPLASH('photo-1570042225831-d98fa7577f1e', 1600);   // cow close-up
const IMG_PASTURE     = UNSPLASH('photo-1516467508483-a7212febe31a', 1600);   // rural landscape
const IMG_FIELDS      = UNSPLASH('photo-1500382017468-9049fed747ef', 1600);   // rolling fields

// Farm work / hands
const IMG_HANDS       = UNSPLASH('photo-1592982537447-7440770cbfc9', 1200);   // farmer holding produce
const IMG_BARN        = UNSPLASH('photo-1595855759920-86582396756a', 1600);   // barn/stall
// IMG_PACKAGING removed — the previously used photo (photo-1573497019940) was
// office/portrait and did not fit the farm theme.

// Products — only the BIO Rindfleisch image is a Unsplash photo.
// The other three product images use SafeImg's themed SVG fallback (waldgrün
// with meat icon) instead of unrelated/broken stock photos. Real product
// photos should be provided by the customer and dropped in here.
const P_BEEF   = UNSPLASH('photo-1588347818481-c9b95f8fa9de', 1200);          // raw beef steak
const P_CHAUB  = '';   // Fallback used (was cocktail/bar photo — removed)
const P_WURST  = UNSPLASH('photo-1601924381810-16b04b8c9c9b', 1200);          // dried sausages hanging
const P_DIRRS  = '';   // Fallback used (was office/portrait photo — removed)

const products = [
  {
    name: 'BIO Rindfleisch',
    subtitle: 'Aus eigener Zucht',
    image: P_BEEF,
    imgAlt: 'Frisches Schweizer BIO Rindfleisch aus eigener Zucht',
    fbIcon: 'meat',
    desc: 'Zartes Fleisch von unseren im Alpstil aufgezogenen Rindern. Cholesterinarm, antibiotikafrei, zu 100 % Schweizer BIO-Qualität.',
    tag: '01',
  },
  {
    name: 'Chaubfleisch',
    subtitle: 'Traditionell luftgetrocknet',
    image: P_CHAUB,
    imgAlt: 'Dünn geschnittenes luftgetrocknetes Chaubfleisch',
    fbIcon: 'meat',
    desc: 'An der frischen Bergluft von Seelisberg schonend luftgetrocknet. Intensiv im Aroma, mager und aus regionaler Verarbeitung.',
    tag: '02',
  },
  {
    name: 'Trochäwirscht',
    subtitle: 'Hausgemachte Trockenwurst',
    image: P_WURST,
    imgAlt: 'Traditionelle Schweizer Trockenwurst Trochäwirscht',
    fbIcon: 'meat',
    desc: 'Nach überliefertem Familienrezept vom Metzger in der Region hergestellt. Kräftig gewürzt, ideal für den Znüni oder das Apéro.',
    tag: '03',
  },
  {
    name: 'Trochäfleisch (Dirrs)',
    subtitle: 'Innerschweizer Klassiker',
    image: P_DIRRS,
    imgAlt: 'Luftgetrocknetes Rindfleisch Trochäfleisch Dirrs in dünnen Scheiben',
    fbIcon: 'meat',
    desc: 'Sorgfältig gepökeltes und luftgetrocknetes Rindfleisch — dünn geschnitten ein Genuss, der die Alpen auf den Teller bringt.',
    tag: '04',
  },
];

const AbHofPage = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroY = useTransform(scrollYProgress, [0, 1], ['0%', '25%']);
  const heroScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.15]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0.4]);

  useEffect(() => {
    // Push a farm-friendly palette on the html root temporarily
    document.documentElement.dataset.theme = 'abhof';
    return () => { delete document.documentElement.dataset.theme; };
  }, []);

  return (
    <div className="abhof-page" style={{
      // Local palette override — waldgrün / creme / beige / anthrazit
      '--hof-cream': '#F5F0E5',
      '--hof-cream-warm': '#EDE5D3',
      '--hof-beige': '#D4C4A4',
      '--hof-green': '#2F4F2C',
      '--hof-green-dark': '#1F3A1D',
      '--hof-green-light': '#5B7A4E',
      '--hof-anthracite': '#1A1A1A',
      '--hof-ink': '#2A2A2A',
      backgroundColor: 'var(--hof-cream)',
      color: 'var(--hof-ink)',
    }}>
      <SEO
        title="Feines Ab Hof · BIO Rindfleisch aus Seelisberg"
        description="Schweizer BIO Rindfleisch, Chaubfleisch, Trochäwirscht und Trochäfleisch – direkt vom Hof der Familie Achermann in Seelisberg (Uri). Antibiotikafrei, aus heimischer Produktion, mit Herz."
        path="/dienstleistungen/abhof"
        image={IMG_HOF_LOCAL}
        jsonLd={[
          serviceLd({
            name: 'Feines Ab Hof – Schweizer BIO Rindfleisch',
            description: 'Direktverkauf ab Hof: BIO Rindfleisch, Chaubfleisch, Trochäwirscht und Trochäfleisch aus Seelisberg (Uri).',
            image: IMG_HOF_LOCAL,
            slug: 'abhof',
          }),
          breadcrumbLd([
            { name: 'Home', path: '/' },
            { name: 'Dienstleistungen', path: '/dienstleistungen' },
            { name: 'Feines Ab Hof', path: '/dienstleistungen/abhof' },
          ]),
        ]}
      />

      {/* ─── Sticky Breadcrumb ─── */}
      <div className="fixed top-0 left-0 right-0 z-40 pointer-events-none">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-[6.5rem] md:pt-[8.5rem]">
          <div
            className="inline-flex items-center gap-3 pointer-events-auto backdrop-blur-md rounded-full pl-2 pr-4 py-1.5 shadow-lg"
            style={{ background: 'rgba(245, 240, 229, 0.85)', border: '1px solid rgba(47, 79, 44, 0.15)' }}
          >
            <Link
              to="/dienstleistungen"
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.3em] font-bold transition-colors"
              style={{ color: 'var(--hof-green-dark)' }}
              data-testid="abhof-breadcrumb-back"
            >
              <ArrowLeft className="w-3 h-3" />
              Dienstleistungen
            </Link>
            <span style={{ color: 'var(--hof-green-light)' }} className="text-xs">/</span>
            <span
              className="text-[10px] uppercase tracking-[0.3em] font-bold italic"
              style={{ color: 'var(--hof-green)' }}
            >
              Feines Ab Hof
            </span>
          </div>
        </div>
      </div>

      {/* ═══════════ HERO ═══════════ */}
      <section ref={heroRef} className="relative min-h-[100vh] flex items-end overflow-hidden">
        <motion.div className="absolute inset-0" style={{ y: heroY, scale: heroScale }}>
          <SafeImg
            src={IMG_HERO_ALPS}
            alt="Schweizer Rinder auf grüner Alpweide mit Bergpanorama bei Seelisberg"
            className="w-full h-full object-cover"
            loading="eager"
            fallbackBg="#2F4F2C"
            fallbackFg="#F5F0E5"
            fallbackIcon="cow"
          />
        </motion.div>
        <motion.div className="absolute inset-0" style={{ opacity: heroOpacity }}>
          <div className="absolute inset-0" style={{
            background: 'linear-gradient(180deg, rgba(26,26,26,0.35) 0%, rgba(26,26,26,0.1) 40%, rgba(31,58,29,0.85) 100%)'
          }}></div>
        </motion.div>

        {/* Grain */}
        <div className="absolute inset-0 pointer-events-none opacity-20 bg-grain"></div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 md:pb-32">
          <Reveal>
            <div className="max-w-3xl">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-px" style={{ background: 'var(--hof-cream)' }}></div>
                <span className="text-[10px] md:text-xs uppercase tracking-[0.4em] font-bold" style={{ color: 'var(--hof-cream)' }}>
                  Dienstleistungen · Feines Ab Hof
                </span>
              </div>

              <h1 className="font-display text-white leading-[0.95] tracking-tight mb-6" style={{ fontSize: 'clamp(3.5rem, 9vw, 8rem)' }}>
                Feines <br />
                <span className="italic" style={{ color: 'var(--hof-cream-warm)' }}>Ab Hof.</span>
              </h1>

              <p className="font-display italic text-2xl md:text-3xl mb-4 leading-tight" style={{ color: 'var(--hof-beige)' }}>
                Aus unserem Hof in Seelisberg.
              </p>
              <p className="text-white/85 text-base md:text-lg leading-relaxed max-w-xl mb-10">
                Schweizer BIO Rindfleisch und traditionelle Spezialitäten aus heimischer Produktion.
                Antibiotikafrei, cholesterinarm — von der Familie Achermann direkt zu Ihnen.
              </p>

              <div className="flex flex-col sm:flex-row gap-3">
                <MagneticButton>
                  <a
                    href="#produkte"
                    className="inline-flex items-center justify-center gap-2 px-8 py-4 text-xs font-bold uppercase tracking-[0.25em] rounded-full transition-colors shadow-2xl"
                    style={{ background: 'var(--hof-cream)', color: 'var(--hof-green-dark)' }}
                    data-testid="abhof-hero-products"
                  >
                    Produkte entdecken
                    <ArrowDown className="w-4 h-4" />
                  </a>
                </MagneticButton>
                <MagneticButton>
                  <a
                    href="#kontakt"
                    className="inline-flex items-center justify-center gap-2 border-2 border-white/60 hover:bg-white/10 text-white px-8 py-4 text-xs font-bold uppercase tracking-[0.25em] rounded-full transition-colors"
                    data-testid="abhof-hero-inquiry"
                  >
                    Anfragen
                    <ArrowRight className="w-4 h-4" />
                  </a>
                </MagneticButton>
              </div>
            </div>
          </Reveal>
        </div>

        {/* Scroll hint */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/70 flex flex-col items-center gap-2 animate-floatY">
          <span className="text-[10px] uppercase tracking-[0.4em]">Scroll</span>
          <div className="w-px h-10" style={{ background: 'var(--hof-cream)' }}></div>
        </div>
      </section>

      {/* ═══════════ INTRO — Editorial ═══════════ */}
      <section className="py-28 md:py-40 px-4 sm:px-6 lg:px-8" style={{ background: 'var(--hof-cream)' }}>
        <div className="max-w-6xl mx-auto grid grid-cols-12 gap-6 md:gap-10 items-end">
          <Reveal direction="right" className="col-span-12 md:col-span-7">
            <div className="text-xs uppercase tracking-[0.4em] font-bold mb-6" style={{ color: 'var(--hof-green-light)' }}>
              01 · Unser Hof
            </div>
            <h2 className="font-display leading-[1.05] tracking-tight mb-8" style={{ fontSize: 'clamp(2.5rem, 6vw, 5rem)', color: 'var(--hof-green-dark)' }}>
              Familie, <span className="italic" style={{ color: 'var(--hof-green)' }}>Land</span>
              <br />
              & Handwerk.
            </h2>
            <p className="text-lg leading-relaxed mb-6" style={{ color: 'var(--hof-ink)' }}>
              Zusammen mit meinem Sohn führe ich hoch über dem Vierwaldstättersee einen kleinen Familienbetrieb.
              Was auf unserem Hof heranwächst, wird bei uns auch verarbeitet — als <em>Generationsgemeinschaft</em>,
              die für gute Nahrungsmittel und einen respektvollen Umgang mit Tier und Natur einsteht.
            </p>
            <p className="text-lg leading-relaxed" style={{ color: 'var(--hof-ink)' }}>
              Direkt ab Hof, mit ehrlichen Preisen — ohne Umwege, ohne Massentierhaltung.
            </p>
          </Reveal>

          <Reveal direction="left" className="col-span-12 md:col-span-5">
            <div className="relative aspect-[4/5] overflow-hidden rounded-sm shadow-2xl" style={{ background: 'var(--hof-green-dark)' }}>
              <SafeImg
                src={IMG_HOF_LOCAL}
                alt="Der Hof der Familie Achermann in Seelisberg"
                className="w-full h-full object-contain"
                fallbackBg="#2F4F2C"
                fallbackFg="#F5F0E5"
                fallbackIcon="farm"
              />
            </div>
            <div className="mt-4 flex items-center gap-3 text-xs uppercase tracking-[0.3em] font-bold" style={{ color: 'var(--hof-green)' }}>
              <MapPin className="w-3.5 h-3.5" />
              Steckenmattstrasse 11, Seelisberg
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══════════ SEELISBERG — Big picture pull-quote ═══════════ */}
      <section className="relative py-24 md:py-32 overflow-hidden" style={{ background: 'var(--hof-green-dark)' }}>
        <div className="absolute inset-0 opacity-30">
          <SafeImg
            src={IMG_LAKE_ALPS}
            alt="Vierwaldstättersee und Berge bei Seelisberg"
            className="w-full h-full object-cover"
            fallbackBg="#1F3A1D"
            fallbackFg="#D4C4A4"
            fallbackIcon="mountain"
          />
        </div>
        <div className="absolute inset-0 bg-grain opacity-25 pointer-events-none"></div>

        <div className="relative z-10 max-w-5xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="inline-flex items-center gap-3 mb-8">
              <Mountain className="w-5 h-5" style={{ color: 'var(--hof-beige)' }} />
              <span className="text-xs uppercase tracking-[0.4em] font-bold" style={{ color: 'var(--hof-beige)' }}>
                02 · Seelisberg · 845 m ü. M.
              </span>
              <Mountain className="w-5 h-5" style={{ color: 'var(--hof-beige)' }} />
            </div>
            <blockquote className="font-display italic text-white leading-[1.1] tracking-tight" style={{ fontSize: 'clamp(2rem, 5vw, 4.5rem)' }}>
              «Wo der Rütli-Blick auf den Vierwaldstättersee fällt,
              <br />
              dort wachsen unsere <span className="not-italic" style={{ color: 'var(--hof-cream-warm)' }}>Rinder</span> auf.»
            </blockquote>
            <div className="mt-10 text-xs uppercase tracking-[0.4em] font-bold" style={{ color: 'var(--hof-beige)' }}>
              Armin Achermann · Seelisberg (UR)
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══════════ ANIMALS & FARMING — asymmetric editorial ═══════════ */}
      <section className="py-28 md:py-40 px-4 sm:px-6 lg:px-8" style={{ background: 'var(--hof-cream)' }}>
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <div className="max-w-2xl mb-16">
              <div className="text-xs uppercase tracking-[0.4em] font-bold mb-4" style={{ color: 'var(--hof-green-light)' }}>
                03 · Tiere & Landwirtschaft
              </div>
              <h2 className="font-display leading-[1.05] tracking-tight" style={{ fontSize: 'clamp(2.25rem, 5vw, 4.25rem)', color: 'var(--hof-green-dark)' }}>
                Aufgewachsen dort,
                <br />
                wo <span className="italic" style={{ color: 'var(--hof-green)' }}>die Luft dünn ist</span>.
              </h2>
            </div>
          </Reveal>

          <div className="grid grid-cols-12 gap-4 md:gap-6">
            <Reveal className="col-span-12 md:col-span-7">
              <div className="relative aspect-[16/10] overflow-hidden rounded-sm shadow-xl">
                <SafeImg
                  src={IMG_COWS_CLOSE}
                  alt="Rinder auf der Alpweide bei Seelisberg"
                  className="w-full h-full object-cover"
                  fallbackBg="#5B7A4E"
                  fallbackFg="#F5F0E5"
                  fallbackIcon="cow"
                />
              </div>
            </Reveal>

            <Reveal delay={120} className="col-span-6 md:col-span-5 md:mt-16">
              <div className="relative aspect-[3/4] overflow-hidden rounded-sm shadow-xl">
                <SafeImg
                  src={IMG_PASTURE}
                  alt="Alpweide über dem Vierwaldstättersee"
                  className="w-full h-full object-cover"
                  fallbackBg="#2F4F2C"
                  fallbackFg="#F5F0E5"
                  fallbackIcon="mountain"
                />
              </div>
            </Reveal>

            <Reveal delay={240} className="col-span-6 md:col-span-4 md:-mt-24">
              <div className="relative aspect-[3/4] overflow-hidden rounded-sm shadow-xl">
                <SafeImg
                  src={IMG_BARN}
                  alt="Landwirtschaftliches Stallgebäude in der Zentralschweiz"
                  className="w-full h-full object-cover"
                  fallbackBg="#3A5A38"
                  fallbackFg="#F5F0E5"
                  fallbackIcon="farm"
                />
              </div>
            </Reveal>

            <Reveal delay={360} className="col-span-12 md:col-span-8">
              <div className="p-8 md:p-12 h-full flex flex-col justify-center" style={{ background: 'var(--hof-cream-warm)' }}>
                <p className="font-display italic text-2xl md:text-3xl leading-tight mb-6" style={{ color: 'var(--hof-green-dark)' }}>
                  «Unsere Tiere leben so, wie es die Natur vorgesehen hat: draussen, im Grünen, mit viel Bewegung.»
                </p>
                <div className="grid grid-cols-3 gap-6">
                  {[
                    { icon: Leaf, label: 'BIO Haltung' },
                    { icon: ShieldCheck, label: 'Antibiotikafrei' },
                    { icon: Heart, label: 'Cholesterinarm' },
                  ].map(({ icon: Icon, label }) => (
                    <div key={label} className="flex flex-col items-start gap-2">
                      <Icon className="w-5 h-5" style={{ color: 'var(--hof-green)' }} />
                      <span className="text-[10px] uppercase tracking-[0.3em] font-bold" style={{ color: 'var(--hof-green-dark)' }}>{label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ═══════════ PRODUCTS ═══════════ */}
      <section id="produkte" className="py-28 md:py-40 px-4 sm:px-6 lg:px-8" style={{ background: 'var(--hof-anthracite)' }}>
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
              <div>
                <div className="text-xs uppercase tracking-[0.4em] font-bold mb-4" style={{ color: 'var(--hof-beige)' }}>
                  04 · Unsere Produkte
                </div>
                <h2 className="font-display leading-[1.05] tracking-tight text-white" style={{ fontSize: 'clamp(2.25rem, 5vw, 4.5rem)' }}>
                  Vier Klassiker,
                  <br />
                  <span className="italic" style={{ color: 'var(--hof-beige)' }}>mit Wurzeln.</span>
                </h2>
              </div>
              <p className="max-w-xs text-sm leading-relaxed" style={{ color: 'var(--hof-cream)' }}>
                Alles aus eigener Aufzucht, in der Region verarbeitet, direkt ab Hof erhältlich.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
            {products.map((p, i) => (
              <Reveal key={p.name} delay={i * 100}>
                <article className="group relative overflow-hidden" style={{ background: 'var(--hof-green-dark)' }} data-testid={`abhof-product-${i}`}>
                  <div className="relative aspect-[5/4] overflow-hidden">
                    <SafeImg
                      src={p.image}
                      alt={p.imgAlt}
                      className="w-full h-full object-cover transition-transform duration-1000 ease-out group-hover:scale-110"
                      fallbackBg="#1F3A1D"
                      fallbackFg="#D4C4A4"
                      fallbackIcon={p.fbIcon}
                    />
                    <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, transparent 30%, rgba(26,26,26,0.85) 100%)' }}></div>
                    <div className="absolute top-6 left-6 font-display text-2xl text-white/80">{p.tag}</div>
                  </div>
                  <div className="p-8 md:p-10">
                    <div className="text-[10px] uppercase tracking-[0.4em] font-bold mb-2" style={{ color: 'var(--hof-beige)' }}>
                      {p.subtitle}
                    </div>
                    <h3 className="font-display text-3xl md:text-4xl text-white mb-4 leading-tight">{p.name}</h3>
                    <p className="text-sm leading-relaxed" style={{ color: 'var(--hof-cream)' }}>{p.desc}</p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════ QUALITY MANIFESTO ═══════════ */}
      <section className="py-28 md:py-40 px-4 sm:px-6 lg:px-8" style={{ background: 'var(--hof-cream-warm)' }}>
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <Reveal direction="right">
            <div className="text-xs uppercase tracking-[0.4em] font-bold mb-4" style={{ color: 'var(--hof-green-light)' }}>
              05 · Qualität
            </div>
            <h2 className="font-display leading-[1.05] tracking-tight mb-8" style={{ fontSize: 'clamp(2.25rem, 5vw, 4.5rem)', color: 'var(--hof-green-dark)' }}>
              Kein Marketing.
              <br />
              <span className="italic" style={{ color: 'var(--hof-green)' }}>Nur Herkunft.</span>
            </h2>

            <div className="space-y-6">
              {[
                { icon: Leaf, title: 'Schweizer BIO', text: 'Zertifizierte biologische Aufzucht — nachprüfbar und ehrlich.' },
                { icon: MapPin, title: 'Heimische Produktion', text: 'Alle Tiere leben, wachsen und werden in der Region verarbeitet.' },
                { icon: ShieldCheck, title: 'Antibiotikafrei', text: 'Wir setzen konsequent auf natürliche Aufzucht ohne Antibiotika.' },
                { icon: Award, title: 'Direkt ab Hof', text: 'Kein Zwischenhandel — Sie erhalten das Produkt direkt von uns.' },
              ].map(({ icon: Icon, title, text }, i) => (
                <div key={title} className="flex items-start gap-5 pt-6" style={{ borderTop: '1px solid rgba(47, 79, 44, 0.15)' }}>
                  <div className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'var(--hof-green)' }}>
                    <Icon className="w-5 h-5" style={{ color: 'var(--hof-cream)' }} />
                  </div>
                  <div>
                    <div className="font-display text-xl mb-1" style={{ color: 'var(--hof-green-dark)' }}>{title}</div>
                    <p className="text-sm leading-relaxed" style={{ color: 'var(--hof-ink)' }}>{text}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal direction="left" delay={200}>
            <div className="relative">
              <div className="relative aspect-[4/5] overflow-hidden rounded-sm shadow-2xl">
                <SafeImg
                  src={IMG_HANDS}
                  alt="Bäuerliche Hände bei der Arbeit mit regionalen Lebensmitteln"
                  className="w-full h-full object-cover"
                  fallbackBg="#2F4F2C"
                  fallbackFg="#F5F0E5"
                  fallbackIcon="farm"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 p-8 max-w-xs shadow-2xl" style={{ background: 'var(--hof-green-dark)', color: 'var(--hof-cream)' }}>
                <p className="font-display italic text-lg leading-tight">«Was wir produzieren, würden wir auch selbst auf den eigenen Tisch bringen.»</p>
                <div className="mt-4 text-[10px] uppercase tracking-[0.3em] font-bold" style={{ color: 'var(--hof-beige)' }}>Familie Achermann</div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══════════ GALLERY — asymmetric editorial ═══════════ */}
      <section className="py-28 md:py-32 px-4 sm:px-6 lg:px-8" style={{ background: 'var(--hof-cream)' }}>
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <div className="max-w-2xl mb-14">
              <div className="text-xs uppercase tracking-[0.4em] font-bold mb-4" style={{ color: 'var(--hof-green-light)' }}>
                06 · Impressionen
              </div>
              <h2 className="font-display leading-[1.05] tracking-tight" style={{ fontSize: 'clamp(2.25rem, 5vw, 4rem)', color: 'var(--hof-green-dark)' }}>
                Ein Tag <span className="italic" style={{ color: 'var(--hof-green)' }}>am Hof</span>.
              </h2>
            </div>
          </Reveal>

          <div className="grid grid-cols-12 gap-3 md:gap-4">
            <Reveal className="col-span-12 md:col-span-8">
              <div className="relative aspect-[16/10] overflow-hidden rounded-sm shadow-lg">
                <SafeImg
                  src={IMG_FIELDS}
                  alt="Weite Felder und Weiden in Seelisberg"
                  className="w-full h-full object-cover"
                  fallbackBg="#5B7A4E"
                  fallbackFg="#F5F0E5"
                  fallbackIcon="mountain"
                />
              </div>
            </Reveal>
            <Reveal delay={80} className="col-span-6 md:col-span-4">
              <div className="relative aspect-[4/5] overflow-hidden rounded-sm shadow-lg">
                <SafeImg
                  src={IMG_COWS}
                  alt="Schweizer Rinder auf der Weide"
                  className="w-full h-full object-cover"
                  fallbackBg="#3A5A38"
                  fallbackFg="#F5F0E5"
                  fallbackIcon="cow"
                />
              </div>
            </Reveal>
            <Reveal delay={160} className="col-span-6 md:col-span-3">
              <div className="relative aspect-square overflow-hidden rounded-sm shadow-lg">
                <SafeImg
                  src={IMG_BARN}
                  alt="Stallgebäude auf einem Schweizer Bauernhof"
                  className="w-full h-full object-cover"
                  fallbackBg="#2F4F2C"
                  fallbackFg="#F5F0E5"
                  fallbackIcon="farm"
                />
              </div>
            </Reveal>
            <Reveal delay={240} className="col-span-6 md:col-span-5">
              <div className="relative aspect-[5/4] overflow-hidden rounded-sm shadow-lg" style={{ background: 'var(--hof-green-dark)' }}>
                <SafeImg
                  src={IMG_HOF_LOCAL}
                  alt="Der Hof der Familie Achermann in Seelisberg"
                  className="w-full h-full object-contain"
                  fallbackBg="#1F3A1D"
                  fallbackFg="#D4C4A4"
                  fallbackIcon="farm"
                />
              </div>
            </Reveal>
            <Reveal delay={320} className="col-span-12 md:col-span-4">
              <div className="p-8 h-full flex flex-col justify-center" style={{ background: 'var(--hof-green)', color: 'var(--hof-cream)' }}>
                <Beef className="w-6 h-6 mb-4" style={{ color: 'var(--hof-cream-warm)' }} />
                <p className="font-display italic text-xl leading-tight mb-4">Vom Rüttli-Blick auf den Vierwaldstättersee — direkt zu Ihnen nach Hause.</p>
                <div className="text-[10px] uppercase tracking-[0.3em] font-bold" style={{ color: 'var(--hof-beige)' }}>Seelisberg · Uri</div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ═══════════ DIRECT SALES / CTA ═══════════ */}
      <section id="kontakt" className="relative py-32 md:py-40 px-4 sm:px-6 lg:px-8 overflow-hidden" style={{ background: 'var(--hof-green-dark)' }}>
        <div className="absolute inset-0 opacity-20">
          <SafeImg
            src={IMG_MOUNTAIN}
            alt=""
            className="w-full h-full object-cover"
            fallbackBg="#1F3A1D"
            fallbackFg="#F5F0E5"
            fallbackIcon="mountain"
          />
        </div>
        <div className="absolute inset-0 bg-grain opacity-20 pointer-events-none"></div>

        <Reveal>
          <div className="relative max-w-4xl mx-auto text-center">
            <div className="text-xs uppercase tracking-[0.4em] font-bold mb-6" style={{ color: 'var(--hof-beige)' }}>
              07 · Direkt ab Hof
            </div>
            <h2 className="font-display leading-[1.02] tracking-tight text-white mb-8" style={{ fontSize: 'clamp(2.5rem, 6vw, 5.5rem)' }}>
              Frisch bestellt.
              <br />
              <span className="italic" style={{ color: 'var(--hof-cream-warm)' }}>Persönlich abgeholt.</span>
            </h2>
            <p className="text-lg md:text-xl leading-relaxed max-w-2xl mx-auto mb-12" style={{ color: 'var(--hof-cream)' }}>
              Melden Sie sich kurz per Telefon oder E-Mail — wir informieren Sie, was aktuell verfügbar ist,
              und vereinbaren einen Termin für die Abholung direkt an der Steckenmattstrasse 11 in Seelisberg.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl mx-auto mb-12">
              <a
                href={`tel:${COMPANY.phoneInternational}`}
                className="group flex flex-col items-start gap-3 p-6 rounded-sm transition-colors"
                style={{ background: 'rgba(245, 240, 229, 0.08)', border: '1px solid rgba(245, 240, 229, 0.15)' }}
                data-testid="abhof-phone"
              >
                <Phone className="w-5 h-5" style={{ color: 'var(--hof-beige)' }} />
                <div className="text-left">
                  <div className="text-[10px] uppercase tracking-[0.3em] font-bold mb-1" style={{ color: 'var(--hof-beige)' }}>Telefon</div>
                  <div className="font-display text-lg text-white">{COMPANY.phone}</div>
                </div>
              </a>
              <a
                href={`mailto:${COMPANY.email}`}
                className="group flex flex-col items-start gap-3 p-6 rounded-sm transition-colors"
                style={{ background: 'rgba(245, 240, 229, 0.08)', border: '1px solid rgba(245, 240, 229, 0.15)' }}
                data-testid="abhof-email"
              >
                <Mail className="w-5 h-5" style={{ color: 'var(--hof-beige)' }} />
                <div className="text-left">
                  <div className="text-[10px] uppercase tracking-[0.3em] font-bold mb-1" style={{ color: 'var(--hof-beige)' }}>E-Mail</div>
                  <div className="text-sm text-white break-all">{COMPANY.email}</div>
                </div>
              </a>
              <div
                className="flex flex-col items-start gap-3 p-6 rounded-sm"
                style={{ background: 'rgba(245, 240, 229, 0.08)', border: '1px solid rgba(245, 240, 229, 0.15)' }}
              >
                <MapPin className="w-5 h-5" style={{ color: 'var(--hof-beige)' }} />
                <div className="text-left">
                  <div className="text-[10px] uppercase tracking-[0.3em] font-bold mb-1" style={{ color: 'var(--hof-beige)' }}>Hof-Adresse</div>
                  <div className="text-sm text-white">Steckenmattstrasse 11<br />6377 Seelisberg</div>
                </div>
              </div>
            </div>

            <MagneticButton>
              <Link
                to="/kontakt"
                className="inline-flex items-center justify-center gap-2 px-10 py-4 text-xs font-bold uppercase tracking-[0.3em] rounded-full transition-colors shadow-2xl"
                style={{ background: 'var(--hof-cream)', color: 'var(--hof-green-dark)' }}
                data-testid="abhof-final-cta"
              >
                Anfrage senden
                <ArrowRight className="w-4 h-4" />
              </Link>
            </MagneticButton>
          </div>
        </Reveal>
      </section>

      {/* ═══════════ FOOTER TRANSITION — Back to services ═══════════ */}
      <div className="py-12 px-4 sm:px-6 lg:px-8 text-center" style={{ background: 'var(--hof-cream)' }}>
        <Link
          to="/dienstleistungen"
          className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.4em] font-bold transition-colors"
          style={{ color: 'var(--hof-green-dark)' }}
          data-testid="abhof-back-services"
        >
          <ArrowLeft className="w-4 h-4" />
          Zurück zu allen Dienstleistungen
        </Link>
      </div>
    </div>
  );
};

export default AbHofPage;
